/**
 * Account management section for the Settings page
 *
 * Shows user info, sign-out button, and inline change password form.
 * Uses a state machine for the change password flow: idle -> form -> success.
 */

import { CheckCircle, KeyRound, LogOut, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ACTIONS } from '@/constants/terminology';
import { extractZodFieldErrors } from '@/lib/utils';
import { changePasswordFormSchema } from '@/schemas/auth';
import { useAuthStore } from '@/stores/authStore';

/** Change password flow steps */
type ChangePasswordStep = 'idle' | 'form' | 'success';

interface AccountSectionProps {
  /** Called when the user clicks Sign Out */
  onLogout: () => void;
}

/**
 * Account section displaying user info with change password and sign-out actions.
 *
 * Only renders content when the user is authenticated.
 */
export function AccountSection({ onLogout }: AccountSectionProps) {
  const { user, isAuthenticated, changePassword } = useAuthStore();

  const [step, setStep] = useState<ChangePasswordStep>('idle');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clean up success timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  if (!isAuthenticated || !user) return null;

  /** Reset the form to idle state */
  function resetForm() {
    setStep('idle');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setErrors({});
    setApiError(null);
    setIsSubmitting(false);
  }

  /** Validate and submit the change password form */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const result = changePasswordFormSchema.safeParse({
      currentPassword,
      newPassword,
      confirmNewPassword
    });

    if (!result.success) {
      setErrors(extractZodFieldErrors(result.error));
      return;
    }

    setErrors({});
    setApiError(null);
    setIsSubmitting(true);

    try {
      await changePassword(result.data.currentPassword, result.data.newPassword);
      setStep('success');
      successTimerRef.current = setTimeout(resetForm, 3000);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="mb-4 p-4 rounded-lg border border-border bg-card">
      {/* User info row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary">
            <User className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{user.username}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
        {step === 'idle' && (
          <Button variant="outline" size="sm" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-1.5" />
            {ACTIONS.SIGN_OUT}
          </Button>
        )}
      </div>

      {/* Change password button (idle state) */}
      {step === 'idle' && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full"
          onClick={() => setStep('form')}
        >
          <KeyRound className="h-4 w-4 mr-1.5" />
          {ACTIONS.CHANGE_PASSWORD}
        </Button>
      )}

      {/* Change password form */}
      {step === 'form' && (
        <form onSubmit={handleSubmit} noValidate className="mt-4 space-y-3">
          {apiError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">
              {apiError}
            </p>
          )}

          <div className="space-y-1.5">
            <label htmlFor="current-password" className="text-sm font-medium">
              Current Password
            </label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
            />
            {errors.currentPassword && (
              <p className="text-xs text-destructive">{errors.currentPassword}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="new-password" className="text-sm font-medium">
              New Password
            </label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            {errors.newPassword && (
              <p className="text-xs text-destructive">{errors.newPassword}</p>
            )}
            {!errors.newPassword && (
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirm-new-password" className="text-sm font-medium">
              Confirm New Password
            </label>
            <Input
              id="confirm-new-password"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
            />
            {errors.confirmNewPassword && (
              <p className="text-xs text-destructive">{errors.confirmNewPassword}</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              variant="accent"
              size="sm"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Password'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              {ACTIONS.CANCEL}
            </Button>
          </div>
        </form>
      )}

      {/* Success state */}
      {step === 'success' && (
        <div className="mt-4 flex items-center gap-2 text-sm text-green-500">
          <CheckCircle className="h-4 w-4" />
          <span>Password updated successfully</span>
        </div>
      )}
    </div>
  );
}
